import type { FC, ReactNode } from 'react'
import type { RegisterUpdater } from '@/components/landing/film/overlay-utils'

import { useEffect, useRef } from 'react'
import { MOMENT, ease, envelope, progress } from '@/components/landing/film/timeline'

import {
    fixed,
    setAttr,
    setLayer,
    setStyle,
    setText
} from '@/components/landing/film/overlay-utils'

interface SceneKineticsProps {
    register: RegisterUpdater
}

const TICKS = Array.from({ length: 72 }, (_, i) => {
    const angle = (i / 72) * Math.PI * 2
    const inner = i % 6 === 0 ? 1.17 : 1.2
    return `M ${fixed(Math.cos(angle) * inner, 4)} ${fixed(Math.sin(angle) * inner, 4)} L ${fixed(Math.cos(angle) * 1.24, 4)} ${fixed(Math.sin(angle) * 1.24, 4)}`
}).join(' ')

const HUD: [number, number] = [9.45, 12.65]
const CAP: [number, number] = [12.75, 13.98]

// Scene 03: holographic HUD around the orbiting wheel, then the floating titanium cap close-up
const SceneKinetics: FC<SceneKineticsProps> = ({ register }): ReactNode => {
    const layerRef = useRef<HTMLDivElement>(null)
    const hudRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const ringGroupRef = useRef<SVGGElement>(null)
    const dottedRef = useRef<SVGCircleElement>(null)
    const ticksRef = useRef<SVGPathElement>(null)
    const arcRef = useRef<SVGCircleElement>(null)
    const degreesRef = useRef<HTMLSpanElement>(null)
    const scanRef = useRef<HTMLDivElement>(null)
    const barRefs = useRef<Array<HTMLSpanElement | null>>([])
    const capRef = useRef<HTMLDivElement>(null)
    const capLineRef = useRef<SVGPathElement>(null)
    const capDotRef = useRef<SVGCircleElement>(null)

    useEffect(() => register((t, real, frame) => {
        const active = t >= HUD[0] && t < CAP[1]
        setLayer(layerRef.current, active ? 1 : 0)
        if (!active) return

        const width = frame.width
        const height = frame.height
        setAttr(svgRef.current, 'viewBox', `0 0 ${Math.round(width)} ${Math.round(height)}`)

        const hud = envelope(t, HUD[0], HUD[1], 0.45, 0.4)
        setStyle(hudRef.current, 'opacity', fixed(hud, 3))
        setStyle(ringGroupRef.current, 'opacity', fixed(hud, 3))
        const wheel = frame.wheel
        setAttr(ringGroupRef.current, 'transform', `translate(${fixed(wheel.x, 1)} ${fixed(wheel.y, 1)}) scale(${fixed(Math.max(1, wheel.r), 2)})`)
        setAttr(dottedRef.current, 'transform', `rotate(${fixed(real * 14, 2)})`)
        setAttr(ticksRef.current, 'transform', `rotate(${fixed(-real * 7, 2)})`)

        const degrees = ease.inOutSine(progress(t, MOMENT.orbitStart, MOMENT.orbitEnd)) * 360
        setStyle(arcRef.current, 'stroke-dasharray', `${fixed(degrees, 2)} 360`)
        setAttr(arcRef.current, 'stroke-width', fixed(2 / Math.max(1, wheel.r), 5))
        setText(degreesRef.current, String(Math.round(degrees)).padStart(3, '0'))

        setStyle(scanRef.current, 'top', `${fixed(((real * 0.42) % 1) * 100, 2)}%`)
        barRefs.current.forEach((bar, i) => {
            const level = 0.35 + 0.65 * Math.abs(Math.sin(real * (1.6 + i * 0.45) + i))
            setStyle(bar, 'transform', `scaleY(${fixed(level, 3)})`)
        })

        const cap = envelope(t, CAP[0], CAP[1], 0.35, 0.25)
        const capEnter = ease.outExpo(progress(t, CAP[0], CAP[0] + 0.7))
        setStyle(capRef.current, 'opacity', fixed(cap, 3))
        setStyle(capRef.current, 'transform', `translateY(${fixed((1 - capEnter) * 16, 2)}px)`)
        setStyle(capRef.current, 'left', frame.portrait ? '7%' : '6%')
        setStyle(capRef.current, 'top', frame.portrait ? '72%' : '64%')

        const x0 = width * (frame.portrait ? 0.07 : 0.06)
        const y0 = height * (frame.portrait ? 0.72 : 0.64) - 10
        const x1 = x0 + width * (frame.portrait ? 0.3 : 0.18)
        const draw = ease.outCubic(progress(t, CAP[0] + 0.2, CAP[0] + 0.85))
        setAttr(capLineRef.current, 'd', `M ${fixed(x0, 1)} ${fixed(y0, 1)} L ${fixed(x1, 1)} ${fixed(y0, 1)} L ${fixed(frame.cap.x, 1)} ${fixed(frame.cap.y, 1)}`)
        setStyle(capLineRef.current, 'stroke-dashoffset', fixed(1 - draw, 4))
        setStyle(capLineRef.current, 'opacity', fixed(cap * 0.85, 3))
        setAttr(capDotRef.current, 'cx', fixed(frame.cap.x, 1))
        setAttr(capDotRef.current, 'cy', fixed(frame.cap.y, 1))
        setStyle(capDotRef.current, 'opacity', fixed(cap * progress(t, CAP[0] + 0.7, CAP[0] + 0.9), 3))
    }), [register])

    return (
        <div ref={layerRef} className='pointer-events-none absolute inset-0' style={{ opacity: 0, visibility: 'hidden' }}>
            <svg ref={svgRef} className='absolute inset-0 size-full overflow-visible' viewBox='0 0 100 100' aria-hidden='true'>
                <g ref={ringGroupRef} style={{ opacity: 0 }}>
                    <circle ref={dottedRef} r='1.13' fill='none' stroke='#c4ccd6' strokeOpacity='0.35' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                    <path ref={ticksRef} d={TICKS} stroke='#ffd700' strokeOpacity='0.55' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                    <circle ref={arcRef} r='1.31' fill='none' stroke='#ffd700' strokeWidth='0.006' pathLength={360} strokeDasharray='0 360' transform='rotate(-90)' />
                    <path d='M -0.06 0 L 0.06 0 M 0 -0.06 L 0 0.06' stroke='#ffd700' strokeOpacity='0.7' strokeWidth='1' vectorEffect='non-scaling-stroke' />
                </g>
                <path ref={capLineRef} d='M 0 0' fill='none' stroke='#ffd700' strokeWidth='1' pathLength={1} strokeDasharray='1' strokeDashoffset='1' style={{ opacity: 0 }} />
                <circle ref={capDotRef} r='3.5' fill='#ffd700' style={{ opacity: 0 }} />
            </svg>

            <div ref={hudRef} className='absolute inset-0' style={{ opacity: 0 }}>
                <div ref={scanRef} className='absolute inset-x-0 h-px bg-linear-to-r from-transparent via-gold/40 to-transparent' />

                <div className='film-hud-col absolute left-[5%]'>
                    <p className='film-eyebrow text-gold'>
                        FEA &middot; Stress distribution
                    </p>
                    <div className='mt-[1.6cqh] flex items-stretch gap-3'>
                        <span className='w-1.5 rounded-full bg-linear-to-b from-[#fff4d0] via-gold to-[#1a4fa8]' style={{ height: '16cqh' }} />
                        <div className='film-mono flex flex-col justify-between text-titanium-light'>
                            <span>Peak load</span>
                            <span>Load paths &middot; 10 spokes</span>
                            <span>Minimum</span>
                        </div>
                    </div>
                </div>

                <div className='film-hud-col absolute right-[5%] text-right'>
                    <p className='film-eyebrow text-gold'>
                        AERO &middot; Brake cooling flow
                    </p>
                    <div className='mt-[1.6cqh] flex h-[7cqh] items-end justify-end gap-1.5'>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <span
                                key={i}
                                ref={(element) => {
                                    barRefs.current[i] = element
                                }}
                                className='h-full w-1.5 origin-bottom bg-[#b8dcff]/80'
                            />
                        ))}
                    </div>
                    <p className='film-eyebrow mt-[3cqh] text-titanium-light'>
                        Rotation
                    </p>
                    <p className='film-value mt-[0.8cqh] text-white'>
                        <span ref={degreesRef}>000</span>
                        <span className='text-gold'>&deg;</span>
                    </p>
                </div>
            </div>

            <div ref={capRef} className='film-cap-label absolute' style={{ opacity: 0, left: '6%', top: '64%' }}>
                <p className='film-eyebrow text-gold'>
                    Floating center cap
                </p>
                <p className='film-heading mt-[1cqh] text-white'>
                    Titanium. Always level.
                </p>
                <p className='film-body mt-[1cqh] text-titanium-light'>
                    Engraved ALIF LAMATO SIGNATURE, it stays upright while the wheel turns beneath it.
                </p>
            </div>
        </div>
    )
}

export default SceneKinetics
